<template>
  <div class="bg-white p-12">
    <FormulateForm v-model="reportForm">
      <div class="border-b border-truegray-200 pb-5 mb-12 flex justify-between">
        <h3 class="mb-0">{{ titleText }}</h3>
      </div>
      <!-- Form content -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
        <!-- Content column 1 -->
        <div class="col-span-1 lg:border-r lg:border-truegray-200 pr-12">
          <h3 class="text-xl mb-8">Engagement details</h3>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FormulateInput
              name="reportName"
              label="Report title"
              validation="required"
              validation-name="Report title"
            />
            <FormulateInput
              type="date"
              name="engagementDate"
              label="Engagement date"
              validation="required"
              validation-name="Engagement date"
            />
          </div>

          <div class="flex items-center justify-between pt-8">
            <h3 class="text-lg m-0">Connections</h3>
            <button
              class="btn btn-trans-black h-8 px-4 m-0"
              @click="showConnectionsModal"
            >
              {{ connectionsActionText }}
            </button>
          </div>
          <div
            v-if="selectedConnectionsUpdated?.length"
            class="gap-4 grid grid-cols-1 md:grid-cols-2 pt-8"
          >
            <ListConnection
              v-for="connection in selectedConnectionsUpdated"
              :key="connection.id"
              :connection="connection"
              :show-full-job-details="!connection.is_manual"
            />
          </div>
          <!-- Connections details -->
          <div class="grid grid-cols-2 gap-4 pt-12 pb-6">
            <div class="col-start-1 col-end-7">
              <FormulateInput
                name="manualContact"
                type="checkbox"
                label="Add connection manually"
                validation-name="Manual connection"
              />
            </div>
            <div
              v-show="reportForm.manualContact"
              class="grid grid-cols-2 gap-4 col-start-1 col-end-7"
            >
              <div class="col-span-1">
                <FormulateInput name="contactName" label="Connection name" />
                <FormulateInput
                  name="contactCompany"
                  label="Connection company / job title"
                />
              </div>
              <div class="col-span-1">
                <FormulateInput name="contactEmail" label="Connection email" />
                <FormulateInput
                  name="contactType"
                  type="select"
                  label="Connection type"
                  :options="connectionTypes"
                />
              </div>
            </div>
          </div>

          <div
            class="grid grid-cols-1 gap-4 py-6 border-b border-truegray-200 lg:border-0"
          >
            <FormulateInput
              name="preferences"
              type="tags"
              label="Engagement types"
              validation-name="Engagement types"
              :options="tagsPreferences"
              :count="reportForm.preferences?.length || 0"
              :is-collapsed="isTagsCollapsed['preferences']"
            />
          </div>
          <div
            class="grid grid-cols-1 gap-4 py-6 border-b border-truegray-200 lg:border-0"
          >
            <FormulateInput
              name="years"
              type="tags"
              label="Year levels"
              validation-name="Year levels"
              :options="tagsYears"
              :count="reportForm.years?.length || 0"
              :is-collapsed="isTagsCollapsed['years']"
            />
          </div>
        </div>
        <div class="col-span-1 lg:pl-6">
          <h3 class="text-xl mb-8">School details</h3>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FormulateInput
              name="staffMember"
              label="Staff member"
              validation-name="Staff member"
            />
            <FormulateInput name="staffEmail" label="Staff email" />
          </div>
          <div
            class="grid grid-cols-1 gap-4 py-6 border-b border-truegray-200 lg:border-0"
          >
            <FormulateInput
              name="subjects"
              type="tags"
              label="Learning areas"
              validation-name="Learning areas"
              :options="tagsSubjects"
              :count="reportForm.subjects?.length || 0"
              :is-collapsed="isTagsCollapsed['subjects']"
            />
          </div>
          <div
            class="grid grid-cols-1 gap-4 py-6 border-b border-truegray-200 lg:border-0"
          >
            <FormulateInput
              name="techSkills"
              type="tags"
              label="Technical skills (hover for description)"
              validation-name="Technical skills"
              :options="tagsTechSkills"
              :count="reportForm.techSkills?.length || 0"
              :is-collapsed="isTagsCollapsed['techSkills']"
            />
          </div>
          <div
            class="grid grid-cols-1 gap-4 py-6 border-b border-truegray-200 lg:border-0"
          >
            <FormulateInput
              name="softSkills"
              type="tags"
              label="Soft skills (hover for description)"
              validation-name="Soft skills"
              :options="tagsSoftSkills"
              :count="reportForm.softSkills?.length || 0"
              :is-collapsed="isTagsCollapsed['softSkills']"
            />
          </div>
          <div
            class="grid grid-cols-1 gap-4 py-6 border-b border-truegray-200 lg:border-0"
          >
            <FormulateInput
              name="teachersNote"
              type="textarea"
              rows="8"
              label="Teacher's note"
            />
          </div>
          <FormulateInput
            type="submit"
            :disabled="isSubmitting"
            :input-class="['w-32 m-0 ml-auto']"
            @click="onSubmit"
          >
            <span
              v-if="isSubmitting"
              class="w-full h-5 flex items-center justify-center"
            >
              <lottie :options="loadingAnimationOptions" />
            </span>
            <span v-else> Submit </span>
          </FormulateInput>
          <!-- FORM END -->
        </div>
      </div>
    </FormulateForm>
  </div>
</template>

<script>
import { defineComponent, computed, watch, ref, onMounted } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import {
  mapArrayStringsToInt,
  convertKeysToSnakeCase,
} from '../../../js/util/helper.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { EVENT_KEYS } from '../../../js/util/constants.js'
import ListConnection from './components/ListConnection.vue'

export default defineComponent({
  name: 'FormUpsertReport',
  components: { ListConnection },

  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isSubmitting = ref(false)

    const isAdmin = computed(() => store.getters['shared/getUserAccess'])
    const isEditing = ref(false)
    const reportId = ref(null)

    const reportForm = ref({
      reportName: '',
      manualContact: false,
      contactName: '',
      contactCompany: '',
      contactEmail: '',
      contactType: null,
      engagementDate: null,
      staffMember: '',
      staffEmail: '',
      teachersNote: '',
      subjects: [],
      preferences: [],
      years: [],
      softSkills: [],
      techSkills: [],
    })

    const checkboxLabelClasses =
      'transform duration-100 ease-linear left-3 top-1 z-40 flex items-center group'

    const checkboxLabelTooltipClasses =
      'absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white'

    const checkboxLabelTooltipText = computed(() => {
      return 'Public connections are visible to anyone browsing CareerWise. Private connections are visible only to school admins.'
    })

    const titleText = computed(() => {
      return isEditing.value ? 'Update Report' : 'Create Report'
    })

    const submitText = computed(() => {
      return isEditing.value ? 'Update Report' : 'Save'
    })

    const tagsSubjects = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsSubjects',
      ),
    )

    const tagsPreferences = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsPreferences',
      ),
    )

    const tagsYears = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsYears',
      ),
    )

    const tagsSoftSkills = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsSoftSkills',
      ),
    )

    const tagsTechSkills = computed(() =>
      store.getters['shared/getTagsMappedForVueFormulate'](
        null,
        'getTagsTechSkills',
      ),
    )

    const isTagsCollapsed = ref({
      subjects: false,
      preferences: false,
      years: false,
      softSkills: false,
      techSkills: false,
    })

    const getInitialTags = (tags) => {
      return store.getters['shared/getTagsMappedForVueFormulate'](
        Object.values(tags),
      ).map((tag) => tag.value.toString())
    }

    const updateInitialTags = (report) => {
      const KEYS = {
        SUBJECTS: 'subjects',
        PREFERENCES: 'preferences',
        YEARS: 'years',
        SOFT_SKILLS: 'softSkills',
        TECH_SKILLS: 'techSkills',
      }
      // @TODO the tags custom input shoud work with props/value but doesn't seem to react,
      // so instead we're using EventBus to update the initial state/selected options
      const initialSubjects = getInitialTags(report.subjects)
      const initialPreferences = getInitialTags(report.preferences)
      const initialYears = getInitialTags(report.years)
      const initialSoftSkills = getInitialTags(report.soft_skills)
      const initialTechSkills = getInitialTags(report.tech_skills)

      const KEYS_OTHER = {
        [KEYS.SUBJECTS]: store.getters['shared/getTagsCategoryKey'](
          KEYS.SUBJECTS,
        ),

        [KEYS.PREFERENCES]: store.getters['shared/getTagsCategoryKey'](
          KEYS.PREFERENCES,
        ),

        [KEYS.YEARS]: store.getters['shared/getTagsCategoryKey'](KEYS.YEARS),

        [KEYS.SOFT_SKILLS]: store.getters['shared/getTagsCategoryKey'](
          KEYS.SOFT_SKILLS,
        ),

        [KEYS.TECH_SKILLS]: store.getters['shared/getTagsCategoryKey'](
          KEYS.TECH_SKILLS,
        ),
      }

      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.SUBJECTS,
        form: reportForm.value,
        selectedOptions: initialSubjects,
        other:
          report.additional_info?.[KEYS_OTHER[KEYS.SUBJECTS]]?.other || null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.PREFERENCES,
        form: reportForm.value,
        selectedOptions: initialPreferences,
        other:
          report.additional_info?.[KEYS_OTHER[KEYS.PREFERENCES]]?.other || null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.YEARS,
        form: reportForm.value,
        selectedOptions: initialYears,
        other: null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.SOFT_SKILLS,
        form: reportForm.value,
        selectedOptions: initialSoftSkills,
        other: null,
      })
      EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
        type: KEYS.TECH_SKILLS,
        form: reportForm.value,
        selectedOptions: initialTechSkills,
        other: null,
      })
    }

    // Connections
    const MANUAL_CONNECTION_KEYS_MAP = {
      contactName: 'name',
      contactCompany: 'company',
      contactEmail: 'email',
      contactType: 'type',
    }

    const connectionTypes = computed(
      () => store.getters['shared/getConnectionsTypesMappedForVueFormulate'],
    )

    const selectedConnectionsUpdated = computed(
      () => store.getters['connections/getReportSelectedConnections'],
    )

    const selectedConnectionsOriginal = ref(selectedConnectionsUpdated.value)

    const manualConnectionUpdated = computed(() => {
      return Object.keys(MANUAL_CONNECTION_KEYS_MAP).reduce(
        (acc, key) => {
          if (key in reportForm.value) {
            const field = MANUAL_CONNECTION_KEYS_MAP[key]
            acc[field] = reportForm.value[key]
          }
          return acc
        },
        {
          is_manual: true,
        },
      )
    })

    const manualConnectionOriginal = computed(() => {
      const connection = selectedConnectionsOriginal.value.filter(
        (connection) => connection.is_manual,
      )

      return connection.length > 0 ? connection : null
    })

    const connectionsActionText = computed(() => {
      const attachText = 'Attach connections',
        modifyText = 'Modify connections'

      if (
        reportForm.value.manualContact &&
        selectedConnectionsUpdated.value.length === 1
      ) {
        // We only have one connection and they are manual
        return attachText
      }

      return selectedConnectionsUpdated.value.length ? modifyText : attachText
    })

    const showConnectionsModal = () => {
      const data = {
        context: 'REPORTS',
      }

      const payload = {
        action: 'attach-connections',
        data: reportId.value ? { ...data, reportId: reportId.value } : data,
      }

      EventBus.$emit('showModal', payload)
    }

    const onSubmit = async () => {
      isSubmitting.value = true

      const payload = convertKeysToSnakeCase(reportForm.value)
      if (reportId.value) {
        payload.id = reportId.value
      }

      payload.tags = mapArrayStringsToInt(
        reportForm.value.subjects
          .concat(reportForm.value.preferences)
          .concat(reportForm.value.years)
          .concat(reportForm.value.softSkills)
          .concat(reportForm.value.techSkills),
      )

      const contactIdsAndTypes = selectedConnectionsUpdated.value.map(
        (connection) => ({ id: connection.id, type: connection.type }),
      )
      payload.contacts = contactIdsAndTypes

      let additionalInfo = null
      const setAdditionalInfo = (type, data) => {
        const key = store.getters['shared/getTagsCategoryKey'](type)
        if (!additionalInfo) {
          additionalInfo = {}
        }

        additionalInfo[key] = {
          other: data,
        }
      }

      if (reportForm.value.preferences_other) {
        setAdditionalInfo('preferences', reportForm.value.preferences_other)
      }

      if (reportForm.value.subjects_other) {
        // @todo eventually the input should support details for each
        // for each selected option (each selected school support tag)
        // not just for 'other'
        setAdditionalInfo('subjects', reportForm.value.subjects_other)
      }

      additionalInfo = additionalInfo ? JSON.stringify(additionalInfo) : ''
      payload.additional_info = additionalInfo

      const success = await store.dispatch(
        'connections/dispatchUpsertReport',
        payload,
      )

      isSubmitting.value = false
      if (success) {
        await store.dispatch(
          'connections/dispatchUpdateSelectedReportConnections',
        )
        const REPORTS_ROUTE = 'ManagerReports'
        router.push({ name: REPORTS_ROUTE })
      }
    }

    const initForm = async () => {
      const ADD_REPORT_ROUTE = 'ManagerAddReport'
      const routeToAddReport = () => {
        router.push({ name: ADD_REPORT_ROUTE })
      }

      if (route.name === 'ManagerUpdateReport') {
        const queryId = parseInt(route.query?.id)
        if (queryId) {
          const report = await store.dispatch(
            'connections/dispatchUpdateReport',
            queryId,
          )

          if (report) {
            // Stay on this route and prefill the form
            isEditing.value = true
            reportId.value = report.id

            reportForm.value.reportName = report.report_name
            reportForm.value.contactName = report.contact_name
            reportForm.value.contactCompany = report.contact_company
            reportForm.value.contactEmail = report.contact_email
            reportForm.value.contactType = report.contact_type
            reportForm.value.engagementDate = report.engagement_date
            reportForm.value.staffMember = report.staff_member
            reportForm.value.staffEmail = report.staff_email
            reportForm.value.teachersNote = report.teachers_note
            selectedConnectionsOriginal.value = report.contacts

            updateInitialTags(report)
            store.dispatch(
              'connections/dispatchUpdateSelectedReportConnections',
              selectedConnectionsOriginal.value,
            )
            if (manualConnectionOriginal.value) {
              reportForm.value.manualContact = true
            }
          } else {
            // Invalid connectionId: route to add connection instead
            routeToAddReport()
          }
        } else {
          // Invalid/no connectionId: route to add connection instead
          routeToAddReport()
        }
      } else {
        isEditing.value = false
        // Init form to default values
        reportForm.value.reportName = ''
        reportForm.value.manualContact = false
        reportForm.value.contactName = ''
        reportForm.value.contactCompany = ''
        reportForm.value.contactEmail = ''
        reportForm.value.contactType = null
        reportForm.value.engagementDate = null
        reportForm.value.staffMember = ''
        reportForm.value.staffEmail = ''
        reportForm.value.teachersNote = ''
        reportForm.value.subjects = []
        reportForm.value.preferences = []
        reportForm.value.years = []
        reportForm.value.softSkills = []
        reportForm.value.techSkills = []
        store.dispatch('connections/dispatchUpdateSelectedReportConnections')
      }
    }

    watch(
      () => route.name,
      () => initForm(),
    )

    watch(
      () => [reportForm.value.manualContact, reportForm.value.contactName],
      () => {
        // Remove any original manual connection from the store
        const updatedConnections = selectedConnectionsUpdated.value.filter(
          (connection) => !connection.is_manual,
        )

        if (reportForm.value.manualContact) {
          // If we are adding a manual contact, we just need to add the updated value to the store,
          // but only if at least their name is set!
          if (reportForm.value.contactName) {
            updatedConnections.push(manualConnectionUpdated.value)
          }
        }

        store.dispatch(
          'connections/dispatchUpdateSelectedReportConnections',
          updatedConnections,
        )
      },
    )

    onMounted(async () => {
      await initForm()

      EventBus.$on(
        EVENT_KEYS.CO_VUE_FORMULATE_TAGS_TOGGLE_COLLAPSED,
        (type) => {
          const value = isTagsCollapsed.value[type]
          isTagsCollapsed.value[type] = !value
        },
      )
    })

    return {
      route,
      loadingAnimationOptions,
      isSubmitting,
      isAdmin,
      isEditing,
      tagsSubjects,
      tagsPreferences,
      tagsYears,
      tagsSoftSkills,
      tagsTechSkills,
      isTagsCollapsed,
      reportForm,
      submitText,
      titleText,
      checkboxLabelClasses,
      checkboxLabelTooltipClasses,
      checkboxLabelTooltipText,
      showConnectionsModal,
      selectedConnectionsUpdated,
      connectionsActionText,
      connectionTypes,
      onSubmit,
    }
  },
})
</script>
